import { Component, inject, input, Input, OnInit, signal, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormService } from '../service';
import { DatePipe } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { CommonModule } from '@angular/common';
import { BookingPriceService } from '../../../../../../services/booking-price.service';
import { StepperService } from '../../../../../../services/stepper.service';
import { BookingConfigService } from '../../../../../../services/booking-config.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateAttributePipe } from '../../../../../../../pipes/translate-attribute/translate-attribute.pipe';

@Component({
  selector: 'app-summary',
  imports: [DatePipe, TranslocoModule, CommonModule, TranslateAttributePipe],
  templateUrl: './summary.component.html',
  styleUrl: './summary.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    'style': 'font-family: Inter, sans-serif;'
  }
})
export class SummaryComponent implements OnInit {
  totalPrice: number = 0
  parkingType!: string;
  form !: FormService
  details !: FormGroup;
  parkingGroup!: FormGroup;
  parkingFrom !: Date;
  arrivalAtParking: any;
  dailyPrice: number = 0;
  site: string = '';
  subscription: any;
  locale: string = "";
  bookingButtonValue: string = "";
  departureFlight: any;
  departureHotel: any;
  arrivalHotel: any;
  additionalServices: any;
  pricingService: BookingPriceService;
  @Input() showBookingButton = true;
  isAdditionalServiceStep = input<boolean>(false);
  freeShuttles: boolean | false = false;
  isAdditionalServicesEnabled = false;
  additionalServicesSelected: any;
  optionsConfig: any;
  private destroy$ = new Subject<void>();
  loadingState: string = 'loading';
  isProcessingPayment: boolean = false;

  constructor(
    private _formService: FormService,
    private translocoService: TranslocoService,
    public _stepperService: StepperService,
    private bookingConfigService: BookingConfigService,
    private cdr: ChangeDetectorRef
  ) {
    const config = this.bookingConfigService.getConfig();
    this.site = config.site;
    this.pricingService = inject(BookingPriceService);
    this.isAdditionalServicesEnabled = this._formService.getForm().get('parking')?.get('selectedParking')?.value?.additionalServicesEnabled ?? false;
    this._formService.getForm().get('parking')?.controls?.selectedParking?.valueChanges?.subscribe((parking: any) => {
      if (parking) {
        this.isAdditionalServicesEnabled = parking.additionalServicesEnabled ? true : false;
        if (this.site == "quality" || this.site == "aiden") {
          this.pricingService.calculateTotal(parking, []);
        }
      } else {
        this.isAdditionalServicesEnabled = false;
      }
    });

    this.bookingButtonValue = this.translocoService.translate('PARKIFLY.SUMMARY.TO_PAYMENT');
    this.subscription = this.translocoService.langChanges$.subscribe((lang) => {
      this.locale = lang;
      this.bookingButtonValue = this.translocoService.translate('PARKIFLY.SUMMARY.TO_PAYMENT');
    });
  }

  public ngOnInit() {
    const stepper = this._stepperService.getStepper();

    if (!this._formService.paymentReference && !this.form?._toThePayment?.getValue()) {
      sessionStorage.removeItem('paymentProcessing');
      sessionStorage.removeItem('orderCreated');
      this.isProcessingPayment = false;
    }

    const config = this.bookingConfigService.getConfig();
    if ('options' in config) {
      const env: any = config;
      if ('detailsFields' in env.options) {
        this.optionsConfig = env.options.detailsFields;
      }
    }
    this.site = config.site;
    this.freeShuttles = config?.freeShuttles;
    this.form = this._formService;
    this.parkingType = this._formService.selectedParking;
    this.details = this.form.getForm().get('details');
    this.parkingGroup = this.form.getForm().get('parkingGroup');
    this.departureHotel = this.form.departureHotel || this.form.getForm().get('hotels')?.controls?.departureHotel?.value;
    this.arrivalHotel = this.form.arrivalHotel || this.form.getForm().get('hotels')?.controls?.arrivalHotel?.value;
    this.additionalServices = this.form.additionalServices;

    if (this.details) {
      this.details.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
      this.details.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }

    this.additionalServices.valueChanges.subscribe((change: any) => {
      this.additionalServicesSelected = this.form.getSelectedServices();
    });

    this.form.selectedDepartureFlight.subscribe((change: any) => {
      if (change) {
        this.parkingFrom = new Date(change.departureTime?.scheduled);
      }
    });

    this.form.updatedDepartureFlight.subscribe((change: any) => {
      if (change) {
        this.departureFlight = change;
      }
    });

    this.form._toThePayment.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value === false && this.isProcessingPayment) {
        this.isProcessingPayment = false;
        sessionStorage.removeItem('paymentProcessing');
      }
    });

    this._formService.paymentReference$.pipe(takeUntil(this.destroy$)).subscribe(paymentRef => {
      if (paymentRef) {
        this.isProcessingPayment = false;
        sessionStorage.removeItem('paymentProcessing');
      }
    });

    // Listen to stepper changes to clear flags only when going back to details
    if (stepper) {
      stepper.selectionChange.pipe(takeUntil(this.destroy$)).subscribe(event => {
        const isOnDetailsStep = event.selectedIndex < stepper.steps.length - 1;
        const wasOnPaymentStep = event.previouslySelectedIndex >= stepper.steps.length - 1;

        // Only clear flags when going back from payment to details
        if (isOnDetailsStep && wasOnPaymentStep) {
          sessionStorage.removeItem('paymentProcessing');
          sessionStorage.removeItem('orderCreated');
          this.isProcessingPayment = false;
        }
      });
    }

  }

  public toPayment(): void {
    // Check if order was already created in this session
    if (sessionStorage.getItem('orderCreated') === 'true') {
      return;
    }

    // Check if payment is already being processed (persistent across component re-init)
    if (sessionStorage.getItem('paymentProcessing') === 'true') {
      return;
    }

    // Disable if payment reference already exists, currently processing, or order already created
    if (this._formService.paymentReference != null || this.isProcessingPayment) {
      return;
    }

    // Check if we're already on payment step to prevent duplicate processing
    const stepper = this._stepperService.getStepper();
    if (stepper && stepper.selectedIndex >= stepper.steps.length - 1) {
      return;
    }

    const isSubscription = sessionStorage.getItem('showSubscription') === 'true';
    const isParkingPermitUser = sessionStorage.getItem('isParkingPermitUser') === 'true';

    if (isSubscription) {
      // For subscription parking, temporarily disable validation for persons and parkingspacesize
      const personsControl = this.details.controls.persons;
      const parkingspacesizeControl = this.details.controls.parkingspacesize;

      personsControl.clearValidators();
      parkingspacesizeControl.clearValidators();
      personsControl.updateValueAndValidity();
      parkingspacesizeControl.updateValueAndValidity();
    }

    if (isParkingPermitUser) {
      // For parking permit users, clear all validators
      Object.keys(this.details.controls).forEach(key => {
        this.details.controls[key].clearValidators();
        this.details.controls[key].updateValueAndValidity();
      });
    }

    this.details.markAllAsTouched();

    if (this.details.valid) {
      this.isProcessingPayment = true;
      sessionStorage.setItem('paymentProcessing', 'true');
      sessionStorage.setItem('orderCreated', 'true');
      this.form._toThePayment.next(true);

      this.loadingState = 'PREPARING_ORDER';
      setTimeout(() => {
        this.loadingState = 'LOADING';
      }, 5000);

      setTimeout(() => {
        this.loadingState = 'ALMOST_THERE';
      }, 15000);

      setTimeout(() => {
        this.loadingState = 'HANG_TIGHT';
      }, 20000);

      // Reset flags after 10 seconds if API fails/hangs
      setTimeout(() => {
        if (this.isProcessingPayment || sessionStorage.getItem('orderCreated') == 'true') {
          this.isProcessingPayment = false;
          sessionStorage.removeItem('paymentProcessing');
          sessionStorage.removeItem('orderCreated');
        }
      }, 10000);
    }
  }

  get isPaymentDisabled(): boolean {
    const isParkingPermitUser = sessionStorage.getItem('isParkingPermitUser') === 'true';
    const isSubscription = sessionStorage.getItem('showSubscription') === 'true';

    // For subscription bookings, only check validity of fields that are actually shown.
    const subscriptionIgnoredFields = ['persons', 'parkingspacesize'];
    const isDetailsInvalidForSubscription = isSubscription && this.details
      ? Object.keys(this.details.controls).some(key =>
        !subscriptionIgnoredFields.includes(key) && this.details.controls[key].invalid
      )
      : false;

    return !this.isOnline ||
      sessionStorage.getItem('orderCreated') === 'true' ||
      this.isProcessingPayment ||
      sessionStorage.getItem('paymentProcessing') === 'true' ||
      (this._formService.paymentReference != null && this._formService.paymentReference != undefined) ||
      (!isParkingPermitUser && !isSubscription && (!this.details || this.details.invalid)) ||
      isDetailsInvalidForSubscription;
  }

  get isPaymentProcessing(): boolean {
    return this.isProcessingPayment || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('paymentProcessing') === 'true');
  }

  get isOnline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
